import { api } from '../api';
import { initAdminShell, escapeHtml, showError, confirmModal, setButtonBusy } from './shell';
import { createPagination, type PagedResult } from './pagination';
import { attachSearch } from './search';
import { t } from '../i18n/i18n';

const ICON_EDIT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
const ICON_TRASH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>';

interface PowerStationRow {
  id: number;
  powerStationId: string | null;
  street: string | null;
  houseNumber: string | null;
  city: string | null;
  capacityValue: string | null;
  connectorTypes: string[];
  organizationId: string | null;
}

const PAGE_SIZE = 25;

(async () => {
  try {
    await initAdminShell('power-stations');
  } catch {
    return;
  }

  const tbody = document.getElementById('ps-tbody');
  if (!tbody) return;

  const pagination = createPagination(1);
  let query = '';

  async function load(page = pagination.page): Promise<void> {
    try {
      const client = await api();
      const { data } = await client.get<PagedResult<PowerStationRow>>('/api/admin/power-stations', {
        params: { page, pageSize: PAGE_SIZE, ...(query ? { search: query } : {}) },
      });
      render(data.items);
      pagination.render('ps-prev', 'ps-next', 'ps-pageinfo', 'ps-pagination', data.total, data.pageSize);
    } catch (err) {
      showError('ps-error', err instanceof Error ? err.message : 'Failed to load power stations.');
    }
  }

  function render(rows: PowerStationRow[]): void {
    if (!tbody) return;
    if (rows.length === 0) {
      tbody.innerHTML = `<tr><td colspan="8" class="req-empty">${escapeHtml(t('admin.power_stations.empty'))}</td></tr>`;
      return;
    }
    const editLabel = t('admin.actions.edit');
    const deleteLabel = t('admin.actions.delete');
    const unassigned = t('admin.organization.unassigned');
    tbody.innerHTML = rows.map((p) => `
      <tr>
        <td>${p.id}</td>
        <td>${escapeHtml(p.powerStationId ?? '')}</td>
        <td>${escapeHtml([p.street, p.houseNumber].filter(Boolean).join(' '))}</td>
        <td>${escapeHtml(p.city ?? '')}</td>
        <td>${escapeHtml(p.capacityValue ?? '')}</td>
        <td>${escapeHtml(p.connectorTypes.join(', '))}</td>
        <td>${escapeHtml(p.organizationId ?? unassigned)}</td>
        <td class="row-actions">
          <a class="row-action" href="/admin-power-station-edit.html?id=${p.id}">${ICON_EDIT}<span>${escapeHtml(editLabel)}</span></a>
          <button type="button" class="row-action row-action--danger" data-id="${p.id}" data-label="${escapeHtml(p.powerStationId ?? String(p.id))}">${ICON_TRASH}<span>${escapeHtml(deleteLabel)}</span></button>
        </td>
      </tr>
    `).join('');

    tbody.querySelectorAll<HTMLButtonElement>('button.row-action--danger').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = Number(btn.dataset.id);
        const label = btn.dataset.label!;
        if (!(await confirmModal({
          title: t('admin.confirm.modal_title_delete'),
          message: t('admin.confirm.delete_power_station').replace('{label}', label),
          confirmLabel: t('admin.confirm.modal_confirm_delete'),
        }))) return;
        setButtonBusy(btn, true);
        try {
          const client = await api();
          await client.delete(`/api/admin/power-stations/${id}`);
          await load();
        } catch (err) {
          showError('ps-error', err instanceof Error ? err.message : 'Delete failed.');
          setButtonBusy(btn, false);
        }
      });
    });
  }

  pagination.attach('ps-prev', 'ps-next', (page) => void load(page));

  const searchInput = document.getElementById('ps-search') as HTMLInputElement | null;
  if (searchInput) {
    attachSearch(searchInput, (q) => {
      query = q;
      pagination.reset();
      void load(1);
    });
  }

  await load();
})();
